import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const TaylorSeriesCalculator = () => {
  const [inputData, setInputData] = useState({
    function: '',
    variable: 'x',
    point: '0',
    degree: '3'
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateTaylorSeries = () => {
    const { function: func, variable, point, degree } = inputData;

    // Validate inputs
    if (!func) {
      setError('Please enter a function');
      return;
    }

    if (!point || isNaN(parseFloat(point))) {
      setError('Please enter a valid point');
      return;
    }

    if (!degree || isNaN(parseInt(degree)) || parseInt(degree) < 0) {
      setError('Please enter a valid non-negative integer for degree');
      return;
    }

    try {
      const a = parseFloat(point);
      const n = parseInt(degree);

      // Create a safe evaluation context
      const mathContext = { ...Math };
      const context = { [variable]: a, ...mathContext };

      // Function to evaluate the input function
      const evaluateFunction = (funcStr, varName, value) => {
        try {
          const evalContext = { [varName]: value, ...mathContext };
          return new Function(`with(this) { return ${funcStr} }`).call(evalContext);
        } catch (err) {
          throw new Error('Invalid function expression');
        }
      };

      // Numerical differentiation using central differences
      const derivative = (funcStr, varName, value, order = 1, h = 1e-5) => {
        if (order === 0) {
          return evaluateFunction(funcStr, varName, value);
        }

        // Central difference formula for higher-order derivatives
        const coeffs = [];
        for (let k = 0; k <= order; k++) {
          const sign = k % 2 === 0 ? 1 : -1;
          coeffs.push(sign * derivative(funcStr, varName, value + (order - k) * h, 0));
        }

        return coeffs.reduce((sum, coeff, i) => sum + coeff, 0) / Math.pow(h, order);
      };

      // Calculate Taylor series coefficients
      const coefficients = [];
      for (let i = 0; i <= n; i++) {
        const coeff = derivative(func, variable, a, i) / factorial(i);
        coefficients.push(coeff);
      }

      // Construct Taylor series polynomial
      let taylorSeries = '0';
      for (let i = 0; i <= n; i++) {
        const term = `${coefficients[i].toFixed(4)}*${variable}`;
        if (i > 0) {
          taylorSeries = `(${taylorSeries}) + (${term}-${a})^${i}`;
        }
      }

      // Create visualization data
      const xValues = [];
      const originalValues = [];
      const taylorValues = [];
      const range = 2; // Range around point a to visualize

      for (let i = 0; i <= 20; i++) {
        const x = a - range + (range * 2 * i) / 20;
        xValues.push(x);
        originalValues.push(evaluateFunction(func, variable, x));
        taylorValues.push(evaluateFunction(taylorSeries, variable, x));
      }

      const chartData = {
        labels: xValues.map((_, i) => `Point ${i + 1}`),
        datasets: [
          {
            label: 'Original Function',
            data: originalValues,
            fill: false,
            tension: 0.1,
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.5)',
          },
          {
            label: 'Taylor Series Approximation',
            data: taylorValues,
            fill: false,
            tension: 0.1,
            borderColor: 'rgba(255, 99, 132, 1)',
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
          }
        ]
      };

      // Create calculation steps
      const calculationSteps = [
        `Step 1: Expand the function ${func} around point ${a} up to degree ${n}`,
        `Step 2: Calculate derivatives up to order ${n}`,
        `Step 3: Evaluate derivatives at point ${a}`,
        `Step 4: Divide by factorial coefficients`,
        `Step 5: Construct Taylor series polynomial`,
        `Step 6: Final Taylor series: ${taylorSeries}`
      ];

      setResult({
        function: func,
        variable,
        point: a,
        degree: n,
        taylorSeries,
        coefficients,
        calculationSteps,
        chartData
      });

    } catch (err) {
      setError(err.message);
    }
  };

  // Helper function to calculate factorial
  const factorial = (n) => {
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  };

  const exampleData = {
    function: 'Math.sin(x)',
    variable: 'x',
    point: 0,
    degree: 5,
    taylorSeries: 'x - (x^3)/6 + (x^5)/120'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-pink-100 to-red-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Taylor Series Calculator | Calculate Taylor Series Expansions</title>
          <meta name="description" content="Calculate Taylor series expansions of mathematical functions around a specific point. This calculator provides the polynomial approximation and visualization of the original function and its expansion." />
          <meta name="keywords" content="taylor series calculator, taylor expansion, polynomial approximation, calculus calculator, math calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🧮</div>
            <h1 className="text-3xl font-bold text-gray-800">Taylor Series Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate Taylor series expansions of mathematical functions around a specific point. This calculator provides the polynomial approximation and visualization of the original function and its expansion.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Function:</label>
              <input
                type="text"
                name="function"
                value={inputData.function}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter function (e.g., Math.sin(x))"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Variable:</label>
              <input
                type="text"
                name="variable"
                value={inputData.variable}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter variable (default: x)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Point (a):</label>
              <input
                type="number"
                name="point"
                value={inputData.point}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter point to expand around"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Degree:</label>
              <input
                type="number"
                name="degree"
                value={inputData.degree}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter degree of expansion"
                min="0"
              />
            </div>
          </div>

          <button
            onClick={calculateTaylorSeries}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Taylor Series
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Taylor Series Expansion</h3>
                  <p className="text-lg">Function: {result.function}</p>
                  <p className="text-lg">Variable: {result.variable}</p>
                  <p className="text-lg">Point: {result.point}</p>
                  <p className="text-lg">Degree: {result.degree}</p>
                  <p className="text-xl font-bold text-pink-600 mt-4">Taylor Series:</p>
                  <pre className="whitespace-pre-wrap mt-2 font-mono">{result.taylorSeries}</pre>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Function Visualization</h3>
                  <div className="h-64 bg-white rounded-lg">
                    <Line
                      data={result.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            title: {
                              display: true,
                              text: 'Value'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: result.variable
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Function vs Taylor Series'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {result.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-pink-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for the Taylor series of sin(x) around 0 up to degree 5:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Function: {exampleData.function}</li>
                <li>Variable: {exampleData.variable}</li>
                <li>Point: {exampleData.point}</li>
                <li>Degree: {exampleData.degree}</li>
                <li>Taylor Series: {exampleData.taylorSeries}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate derivatives of sin(x) at 0</li>
                <li>Divide each derivative by the corresponding factorial</li>
                <li>Construct the polynomial using the coefficients</li>
                <li>The result is the Taylor series approximation</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-pink-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Taylor Series Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Function:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the mathematical function using JavaScript syntax</li>
                  <li>Example: Math.sin(x), Math.exp(x), x**2 + 3*x + 2</li>
                </ul>
              </li>
              <li><strong>Set Variable:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Specify the variable used in the function</li>
                </ul>
              </li>
              <li><strong>Enter Point:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the point around which to expand the function</li>
                </ul>
              </li>
              <li><strong>Set Degree:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Specify the degree of the Taylor polynomial</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The Taylor series expansion</li>
                  <li>A visualization comparing the original function and its approximation</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-pink-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Higher degree polynomials provide better approximations near the expansion point</li>
                <li>The approximation accuracy decreases as you move away from the expansion point</li>
                <li>Use Math. for mathematical functions (e.g., Math.sin(x), Math.exp(x))</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Taylor Series <span className="text-pink-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              A Taylor series is a representation of a function as an infinite sum of terms calculated from the function's derivatives at a single point. The general formula for a Taylor series expansion of a function f(x) around a point a is:
            </p>
            <p className="font-bold text-xl mt-4">f(x) ≈ Σ [f⁽ⁿ⁾(a) / n!] × (x - a)ⁿ</p>
            <p className="mt-2">
              Where:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>f⁽ⁿ⁾(a):</strong> The nth derivative of f evaluated at point a</li>
                <li><strong>n!:</strong> Factorial of n</li>
                <li><strong>(x - a)ⁿ:</strong> The nth power of (x - a)</li>
              </ul>
            </p>
            <p className="mt-4">
              Taylor series have numerous applications in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Mathematics:</strong> Function approximation and analysis</li>
                <li><strong>Physics:</strong> Modeling physical phenomena</li>
                <li><strong>Engineering:</strong> System modeling and control theory</li>
                <li><strong>Numerical Analysis:</strong> Solving differential equations</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-pink-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Choose Appropriate Degree:</strong> Higher degrees provide better approximations but require more computation</li>
              <li><strong>Evaluate Near Expansion Point:</strong> The approximation is most accurate near the expansion point</li>
              <li><strong>Understand Convergence:</strong> Taylor series may not converge for all x values, especially near singularities</li>
              <li><strong>Use for Smooth Functions:</strong> Taylor series work best for functions that are infinitely differentiable</li>
            </ul>
            <p className="italic mt-4">
              Note: Numerical methods for calculating derivatives can introduce errors, especially for higher-order terms.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TaylorSeriesCalculator;